/**
 * B6 — Análisis año vs. año
 * Gráfico de líneas (año actual vs anteriores) + barras agrupadas por mes.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Card } from '../ui/card';
import { analyticsComercialesApi } from '../../services/api';
import { Loader2, TrendingUp } from 'lucide-react';

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const COLORES = ['#6366f1','#10b981','#f59e0b','#ef4444'];

const fmtEur = (v) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

const TooltipCustom = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded shadow-md px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {fmtEur(p.value)}</p>
      ))}
    </div>
  );
};

export default function GraficoAnioVsAnio({ empresaGlobal }) {
  const [empresa, setEmpresa] = useState(empresaGlobal || '');
  const [familia, setFamilia] = useState('');
  const [familiaInput, setFamiliaInput] = useState('');
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);

  // Sincronizar empresa global
  useEffect(() => { setEmpresa(empresaGlobal || ''); }, [empresaGlobal]);

  const cargar = useCallback(async (emp, fam) => {
    setLoading(true);
    try {
      const res = await analyticsComercialesApi.anioVsAnio(emp || undefined, fam || undefined);
      setDatos(res);
    } catch { setDatos(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(empresa, familia); }, [empresa, familia, cargar]);

  // Transformar rows → array por mes con una key por año
  const chartData = React.useMemo(() => {
    if (!datos) return [];
    const anios = datos.anios || [];
    const byMesAnio: Record<string, Record<number, number>> = {};
    for (let m = 1; m <= 12; m++) byMesAnio[m] = {};
    for (const r of datos.rows || []) {
      if (!byMesAnio[r.mes]) byMesAnio[r.mes] = {};
      byMesAnio[r.mes][r.anio] = r.importe;
    }
    return Array.from({ length: 12 }, (_, i) => {
      const mes = i + 1;
      const entry: Record<string, unknown> = { mes: MESES[i] };
      for (const a of anios) entry[String(a)] = byMesAnio[mes]?.[a] ?? null;
      return entry;
    });
  }, [datos]);

  const anios = datos?.anios || [];

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-semibold text-ink-900 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-brand-500" /> Facturación año vs. año
        </h3>
        <div className="flex items-center gap-2 text-sm">
          <select value={empresa} onChange={e => setEmpresa(e.target.value)}
            className="border rounded px-2 py-1 text-xs">
            <option value="">Todas las empresas</option>
            <option value="platecsa">Platecsa</option>
            <option value="aguazor">Aguazor</option>
          </select>
          <input
            placeholder="Familia (3 chars, ej. G07)"
            value={familiaInput}
            onChange={e => setFamiliaInput(e.target.value.toUpperCase().slice(0, 3))}
            onKeyDown={e => e.key === 'Enter' && setFamilia(familiaInput)}
            className="border rounded px-2 py-1 text-xs w-40"
            maxLength={3}
          />
          <button onClick={() => setFamilia(familiaInput)}
            className="px-2 py-1 text-xs bg-brand-500 text-white rounded hover:bg-brand-600">
            Aplicar
          </button>
          {familia && (
            <button onClick={() => { setFamilia(''); setFamiliaInput(''); }}
              className="px-2 py-1 text-xs border rounded hover:bg-slate-50">
              ✕ {familia}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Calculando…
        </div>
      ) : chartData.length === 0 ? (
        <p className="text-center text-slate-400 py-8">Sin datos de facturación.</p>
      ) : (
        <div className="space-y-8">
          {/* Líneas — una por año */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Evolución mensual (€)</p>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} tick={{ fontSize: 11 }} width={60} />
                <Tooltip content={<TooltipCustom />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {anios.map((a, i) => (
                  <Line
                    key={a}
                    type="monotone"
                    dataKey={String(a)}
                    name={String(a)}
                    stroke={COLORES[i % COLORES.length]}
                    strokeWidth={a === new Date().getFullYear() ? 2.5 : 1.5}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Barras agrupadas — volumen absoluto */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Volumen absoluto mensual (€)</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} tick={{ fontSize: 11 }} width={60} />
                <Tooltip content={<TooltipCustom />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {anios.map((a, i) => (
                  <Bar key={a} dataKey={String(a)} name={String(a)}
                    fill={COLORES[i % COLORES.length]}
                    fillOpacity={a === new Date().getFullYear() ? 1 : 0.65}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Card>
  );
}
